import {
    usePortfolioActiveInputUsesOrdersStream,
    usePortfolioSelection,
    usePortfolioStreamedOrSelectedPositionFromOrders,
    usePortfolioStreamedOrSelectedSymbolFromOrders,
    useSelectPortfolioStreamedOrSelectedOrders,
} from 'portfolio/store/hooks';
import { PortfolioSelectionRequirements } from 'widgets/PortfolioSelectionRequirements';
import { useServices } from 'app/hooks';
import { useWidgetContext } from 'widgets/WidgetContext';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import {
    getCategoriedWidgets,
    WidgetDropdownCategory,
} from 'widgets/WidgetCategory';
import { WidgetDropdownItem } from 'widgets/WidgetsService';
import { useSelectAppMode } from 'app/store/general/selectors';
import { WidgetHost } from 'widgets/WidgetRegistry';

export function useWidgetsService() {
    const { widgetsService } = useServices();
    return widgetsService;
}

export function useWidgetPortfolioSelectionRequirements() {
    const { widgetType } = useWidgetContext();
    const widgetsService = useWidgetsService();
    const selectionRequirements =
        widgetsService.getPortfolioSelectionRequirements(widgetType);
    const selectionRequirementsWithoutOrdersStream =
        widgetsService.getPortfolioSelectionRequirementsWithoutOrdersStream(
            widgetType
        );
    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();

    if (
        !activeInputUsesOrdersStream &&
        selectionRequirementsWithoutOrdersStream !== undefined
    ) {
        return selectionRequirementsWithoutOrdersStream;
    }

    return selectionRequirements;
}

export type PortfolioSelectionRequirementsStatus =
    | 'OK'
    | 'NO_SELECTION'
    | 'ORDERS_WITHOUT_SYMBOLS'
    | 'TOO_MANY';

export interface UseWidgetPortfolioSelectionStatusResult {
    portfolioSelectionRequirements: PortfolioSelectionRequirements;
    hasValidSelection: boolean;
}

export const useWidgetPortfolioSelectionStatus: () => UseWidgetPortfolioSelectionStatusResult =
    () => {
        const portfolioSelectionRequirements =
            useWidgetPortfolioSelectionRequirements();
        const hasValidSelection =
            useWidgetPortfolioSelectionRequirementsStatus() === 'OK';

        return {
            portfolioSelectionRequirements,
            hasValidSelection,
        };
    };
export const useWidgetHasValidSelection = () =>
    useWidgetPortfolioSelectionRequirementsStatus() === 'OK';

export function useWidgetPortfolioSelectionRequirementsStatus(): PortfolioSelectionRequirementsStatus {
    const requirements = useWidgetPortfolioSelectionRequirements();
    return usePortfolioSelectionRequirementsStatus(requirements);
}

export function usePortfolioSelectionRequirementsStatus(
    requirements: PortfolioSelectionRequirements
): PortfolioSelectionRequirementsStatus {
    const portfolioSelection = usePortfolioSelection();
    const wrapper = new PortfolioSelectionWrapper(portfolioSelection);
    const ordersStreamedPosition =
        usePortfolioStreamedOrSelectedPositionFromOrders();
    const ordersStreamedSymbol =
        usePortfolioStreamedOrSelectedSymbolFromOrders();
    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();
    const streamedOrSelectedOrders =
        useSelectPortfolioStreamedOrSelectedOrders();

    const haveNonBlankSymbol = !!(
        wrapper.selectedSymbolQuantities.length > 0 &&
        wrapper.selectedSymbolQuantities.find((s) => !!s.symbol)
    );

    switch (requirements) {
        case PortfolioSelectionRequirements.ExactlyOneOrder: {
            if (
                !streamedOrSelectedOrders ||
                streamedOrSelectedOrders.length === 0
            ) {
                return 'NO_SELECTION';
            }
            if (streamedOrSelectedOrders.length > 1) {
                return 'TOO_MANY';
            }
            return 'OK';
        }
        case PortfolioSelectionRequirements.AtLeastOneOrder: {
            if (
                !streamedOrSelectedOrders ||
                streamedOrSelectedOrders.length === 0
            ) {
                return 'NO_SELECTION';
            }
            return 'OK';
        }
        case PortfolioSelectionRequirements.PositionFromAtLeastOneOrder: {
            if (ordersStreamedPosition) {
                return 'OK';
            }
            if (
                !streamedOrSelectedOrders ||
                streamedOrSelectedOrders.length === 0
            ) {
                return 'NO_SELECTION';
            }

            if (streamedOrSelectedOrders.length === 1) {
                return 'OK';
            }
            return 'TOO_MANY';
        }
        case PortfolioSelectionRequirements.SymbolFromAtLeastOneOrder: {
            if (ordersStreamedSymbol) {
                return 'OK';
            }
            if (
                !streamedOrSelectedOrders ||
                streamedOrSelectedOrders.length === 0
            ) {
                return 'NO_SELECTION';
            }
            if (streamedOrSelectedOrders.length === 1) {
                return 'OK';
            }
            return 'TOO_MANY';
        }
        case PortfolioSelectionRequirements.SingleSymbolQuantity: {
            const result = returnForSingleRequirementFromArray(
                portfolioSelection.selectedSymbolQuantities
            );
            if (result === 'OK' && !haveNonBlankSymbol) {
                return 'ORDERS_WITHOUT_SYMBOLS';
            }
            return result;
        }
        case PortfolioSelectionRequirements.AtLeastOneSymbolQuantity:
            return portfolioSelection.selectedSymbolQuantities.length === 0
                ? 'NO_SELECTION'
                : 'OK'; // Intraday Profiles deals with missing symbols (reverts to Instinet symbols)
        case PortfolioSelectionRequirements.SingleSymbol: {
            const result = returnForSingleRequirementFromArray(
                portfolioSelection.selectedSymbols
            );
            if (result === 'OK' && !haveNonBlankSymbol) {
                return 'ORDERS_WITHOUT_SYMBOLS';
            }
            return result;
        }
        case PortfolioSelectionRequirements.Portfolio: {
            return activeInputUsesOrdersStream ? 'OK' : 'NO_SELECTION';
        }
    }

    return 'NO_SELECTION';
}

function returnForSingleRequirementFromArray(
    arr: Array<unknown>
): PortfolioSelectionRequirementsStatus {
    if (arr.length === 0) {
        return 'NO_SELECTION';
    }
    if (arr.length === 1) {
        return 'OK';
    }
    return 'TOO_MANY';
}

export function useCategorisedWidgets(
    host: WidgetHost,
    dropdownCategories: WidgetDropdownCategory[]
): Array<{
    category: WidgetDropdownCategory;
    options: WidgetDropdownItem[];
}> {
    const widgetsService = useWidgetsService();
    const appMode = useSelectAppMode();

    return getCategoriedWidgets(
        widgetsService,
        appMode,
        host,
        dropdownCategories
    );
}
